import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { MEET_NOTES_COPY } from "./shared/useMeetNotesOffer";
import "./WhatsNewPane.css";

const formatReleaseDate = (iso) => {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Gemini-notes action area: Allow access (with Not now) while the offer applies; otherwise
 * (granted, skipped, or no offer from the server) a way to the Google Meet connector.
 */
const MeetNotesAction = ({ meet, tip, onOpenMeetSettings }) => {
  const { phase, errorText } = meet || {};
  const offered = Boolean(meet?.workflow);
  return (
    <div className="whats-new__action">
      {offered && (phase === "offer" || phase === "error") ? (
        <>
          {phase === "error" ? (
            <p className="whats-new__item-note">{errorText || MEET_NOTES_COPY.error}</p>
          ) : null}
          <div className="whats-new__action-row">
            <button type="button" className="whats-new__primary" onClick={() => void meet.allow()}>
              {phase === "error" ? "Try again" : "Allow access"}
            </button>
            <button type="button" className="whats-new__link" onClick={meet.dismiss}>
              Not now
            </button>
          </div>
        </>
      ) : offered && phase === "done" ? (
        <>
          <p className="whats-new__item-note">{MEET_NOTES_COPY.done}</p>
          <button type="button" className="whats-new__link" onClick={meet.dismiss}>
            Got it
          </button>
        </>
      ) : offered ? (
        <>
          <p className="whats-new__item-note">{MEET_NOTES_COPY[phase]}</p>
          <button type="button" className="whats-new__primary" disabled>
            {phase === "opening" ? "Opening…" : "Waiting for Google…"}
          </button>
        </>
      ) : (
        <div className="whats-new__action-row">
          <button type="button" className="whats-new__primary" onClick={onOpenMeetSettings}>
            Open Google Meet settings
          </button>
        </div>
      )}
      {tip ? <p className="whats-new__item-note">{tip}</p> : null}
    </div>
  );
};

/**
 * Quick-capture action. Desktop: Turn on, then Open System Settings until macOS grants it,
 * then Open Settings (macOS Accessibility settings, to review or revoke). Browser: get the desktop app.
 */
const QuickCaptureAction = ({ capture, isDesktopApp, desktopAppUrl, tip }) => {
  const { state, busy, setEnabled, openAccessibilitySettings } = capture;
  const needsPermission = state.enabled && !state.accessibilityTrusted;
  return (
    <div className="whats-new__action">
      <div className="whats-new__action-row">
        {!isDesktopApp ? (
          <a
            className="whats-new__primary whats-new__primary--link"
            href={desktopAppUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get the desktop app
          </a>
        ) : needsPermission ? (
          <button type="button" className="whats-new__primary" onClick={openAccessibilitySettings}>
            Open System Settings
          </button>
        ) : state.enabled ? (
          <button type="button" className="whats-new__primary" onClick={openAccessibilitySettings}>
            Open Settings
          </button>
        ) : (
          <button
            type="button"
            className="whats-new__primary"
            disabled={busy}
            onClick={() => void setEnabled(true)}
          >
            {busy ? "Turning on…" : "Turn on"}
          </button>
        )}
      </div>
      <p className="whats-new__item-note">
        {isDesktopApp ? (state.enabled && state.accessibilityTrusted ? "On." : tip) : "Desktop app only."}
      </p>
    </div>
  );
};

/**
 * Centered popup listing the action items by version, opened from the account menu. Versions
 * with something to act on stay open; plain version history is a closed accordion. Entries
 * stay listed; the ones the user had not opened before get a purple dot while it is open.
 * Closes with ×, Esc or a click on the dimmed page.
 *
 * @example
 * <WhatsNewPane open releases={visibleReleases()} freshIds={["add-filter"]} onClose={close} meet={meet} />
 */
const WhatsNewPane = ({
  open,
  releases,
  freshIds = [],
  onClose,
  meet = null,
  capture = null,
  isDesktopApp = false,
  desktopAppUrl = "",
  onOpenMeetSettings = () => {},
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    const onPointerDown = (event) => {
      if (event.target instanceof Element && !event.target.closest(".whats-new-pane")) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  const fresh = new Set(freshIds);

  return createPortal(
    <div className="whats-new-backdrop">
    <aside className="whats-new-pane" role="dialog" aria-modal="true" aria-label="What's new">
      <div className="whats-new-pane__header">
        <h2 className="whats-new-pane__title">What&apos;s new</h2>
        <button type="button" className="whats-new-pane__close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="whats-new-pane__body">
        {releases.length === 0 ? (
          <p className="whats-new__empty">You&apos;re all caught up. Nothing needs your attention.</p>
        ) : null}
        {releases.map((release) => {
          const heading = (
            <>
              <span>v{release.version}</span>
              {formatReleaseDate(release.date) ? (
                <span className="whats-new__date">{formatReleaseDate(release.date)}</span>
              ) : null}
            </>
          );
          const items = release.items.map((item) => (
            <div key={item.id} className="whats-new__item" data-feature={item.id}>
              <p className="whats-new__item-title">
                {fresh.has(item.id) ? (
                  <span className="whats-new-dot whats-new-dot--inline" aria-label="New" />
                ) : null}
                {item.title}
              </p>
              <p className="whats-new__item-desc">{item.description}</p>
              {item.id === "meet-gemini-notes" ? (
                <MeetNotesAction meet={meet} tip={item.tip} onOpenMeetSettings={onOpenMeetSettings} />
              ) : item.id === "quick-capture" && capture ? (
                <QuickCaptureAction
                  capture={capture}
                  isDesktopApp={isDesktopApp}
                  desktopAppUrl={desktopAppUrl}
                  tip={item.tip}
                />
              ) : null}
            </div>
          ));
          // Nothing to act on in this version: a closed accordion, opened on demand.
          if (release.items.every((item) => item.info)) {
            return (
              <details key={release.version} className="whats-new__release whats-new__release--history">
                <summary className="whats-new__version whats-new__version--toggle">{heading}</summary>
                {items}
              </details>
            );
          }
          return (
            <section key={release.version} className="whats-new__release">
              <h3 className="whats-new__version">{heading}</h3>
              {items}
            </section>
          );
        })}
      </div>
    </aside>
    </div>,
    document.body
  );
};

export default WhatsNewPane;
